class BlockBuilder extends HTMLElement {
  constructor() {
    super();
    this.backButton = this.querySelector('#bb-back-button');
    this.nextButton = this.querySelector('#bb-next-button');
    this.skipButton = this.querySelector('#bb-skip-button');
    this.addToCartButton = this.querySelector('#bb-add-to-cart-button');
    this.subtotal = this.querySelector('#bb-subtotal');
    this.cartCount = this.querySelector('#bb-cart-count');
    this.productModal = this.querySelector('#bb-product-modal');
    this.continueWarningModal = this.querySelector('#bb-warning-modal-continue');
    this.pickUpWarningModal = this.querySelector('#bb-warning-modal-pick-up');
    this.changeBlockWarningModal = this.querySelector('#bb-warning-modal-change-block');
    
    this.navigation = this.querySelector('#bb-navigation');
    this.navigationItems = this.navigation.querySelectorAll('button');

    this.featuredImageContainer = this.querySelector('#bb-featured-image-container');
    this.seriesImage = this.querySelector('#bb-series-image');
    this.blockSelectorImage = this.querySelector('#bb-block-selector-image');
    this.mobileBlockSelectorImage = this.querySelector('#bb-mobile-block-image');
    this.slotSelectorImage = this.querySelector('#bb-slot-selector');
    this.mobileSlotSelectorImage = this.querySelector('#bb-mobile-slot-selector');
    this.imageContainers = [
      this.seriesImage,
      this.blockSelectorImage,
      this.slotSelectorImage,
      this.mobileSlotSelectorImage,
    ];
  
    this.seriesPage = this.querySelector('#bb-series');
    this.blocksPage = this.querySelector('#bb-blocks');
    this.knivesPage = this.querySelector('#bb-knives');
    this.reviewPage = this.querySelector('#bb-review');
    this.pages = [
      this.seriesPage,
      this.blocksPage,
      this.knivesPage,
      this.reviewPage,
    ];

    this.blockCards = this.querySelector('#bb-block-cards');
    this.blockSelection = this.querySelector('#bb-block-selection');

    // Initialize user cache
    this.userCache = new BlockBuilderUserCache();

    this.gtmEvents = {
      'start': 'bb_step_series_start',
      'block-selected': 'bb_stepblock_complete',
      'series-selected': 'bb_step_series_complete',
      'knives-selected': 'bb_stepknives_complete',
      'add-to-cart': 'bb_stepreview_complete',
    };
  }

  currentPage = null;
  selectedSeries = null;
  selectedBlockStyle = null;
  blockSelectionIsOpen = false;
  currentBlockSelector = null;
  blockDiscountPercentage = 0;
  totalSlotCount = 0;
  blockBuilderCart = {
    items: [],
    total: 0,
    totalQuantity: 0,
  };
  currentProductModalHandle = null;
  slotSelector = null;
  usingKeyboard = false;

  connectedCallback() {
    // Track keyboard vs mouse usage
    document.addEventListener('keydown', () => { this.usingKeyboard = true; });
    document.addEventListener('mousedown', () => { this.usingKeyboard = false; });
    
    // Navigation
    this.navigationItems.forEach(item => {
      item.addEventListener('click', this.onNavigationClick.bind(this));
    });

    this.backButton.addEventListener('click', this.onBackButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onNextButtonClick.bind(this));
    this.skipButton.addEventListener('click', this.onSkipButtonClick.bind(this));

    // Series
    this.seriesRadios = this.seriesPage.querySelectorAll('input[name="bb_series"]');

    // Blocks
    this.blockStyleRadios = this.blockCards.querySelectorAll('input[name="bb_block_style"]');
    this.blockStyleRadios.forEach(radio => {
      radio.addEventListener('change', this.onBlockStyleChange.bind(this));
    });
    this.blockSelectors = this.blockSelection.querySelectorAll('bb-block-selector');
    this.blockSelectors.forEach(selector => {
      selector.addEventListener('block-selected', this.onBlockSelected.bind(this));
    });

    // Knives
    this.knifeSelector = this.knivesPage.querySelector('bb-knife-selector');
    this.knifeSelector.addEventListener('bb-knife-qty-changed', this.onKnifeQtyChange.bind(this));
    this.knifeSelector.addEventListener('bb-slot-selected', this.onSlotSelected.bind(this));

    // Slot Selector
    this.slotSelectorContainer = this.querySelector('#bb-slot-selector-container');
    this.mobileSlotSelectorContainer = this.querySelector('#bb-mobile-slot-selector-container');
    this.slotInfoBar = this.querySelector('#bb-slot-info-bar');
    this.slotInfoBarCurrentSlot = this.querySelector('#bb-slot-info-bar-current-slot');
    this.slotInfoBarCurrentSlotMobile = this.querySelector('#bb-slot-info-bar-current-slot-mobile');

    // Cart
    this.bbCartDrawer = this.querySelector('block-builder-cart-drawer');
    this.bbCartDrawer.addEventListener('cart-item-removed', this.onCartItemRemoved.bind(this));
    this.bbCartDrawer.addEventListener('change-block', this.onChangeBlock.bind(this));

    // Review
    this.reviewItems = this.reviewPage.querySelector('#bb-review-items');
    this.addToCartButton.addEventListener('click', this.onAddToCart.bind(this));
    this.reviewErrors = this.reviewPage.querySelector('#bb-review-errors');
    this.reviewAddMoreText = this.reviewPage.querySelector('#bb-review-add-more-text');
    this.reviewAddMoreButton = this.reviewAddMoreText.querySelector('button');
    this.reviewAddMoreButton.addEventListener('click', this.onReviewAddMoreButtonClick.bind(this));

    // Save state when user leaves the page
    this.shouldSaveStateOnLeave = true;
    window.addEventListener('beforeunload', () => {
      if (shouldSaveStateOnLeave) {
        this.saveUserState();
      }
    });

    // Responsive slot selector handling
    this.handleSlotSelectorResponsive();
    window.addEventListener('resize', this.handleSlotSelectorResponsive.bind(this));

    // Initialize
    this.currentPage = this.seriesPage;
    this.selectedSeries = null;
    this.selectedBlockStyle = this.blockStyleRadios[0].value;
    this.blockDiscountPercentage = this.dataset.bbDiscountPercentage;

    // Restore user state from cache
    this.openWarningModal('pick-up')
    .then(() => {
      this.restoreUserState();
      this.updatePage(this.currentPage);
    })
    .catch(() => {
      this.clearAllCachedData();
    });
    
    this.updatePage(this.currentPage);

    setTimeout(() => {
      window.pushToDataLayer({
        event: this.gtmEvents.start,
      });
    }, 1000);
  }

  updateBlockSelectorImage(imageSrc) {
    const imageTag = this.blockSelectorImage.querySelector('img');
    imageTag.src = imageSrc;
    imageTag.srcset = generateSrcset(imageSrc);
    
    // Update mobile block selector image as well
    const mobileImageTag = this.mobileBlockSelectorImage.querySelector('img');
    if (mobileImageTag) {
      mobileImageTag.src = imageSrc;
      mobileImageTag.srcset = generateSrcset(imageSrc);
    }
  }

  updateCartCount(count) {
    this.cartCount.textContent = this.cartCount.textContent.replace(/\d+/g, count);
  }

  updateSubtotal(subtotal) {
    if (this.blockBuilderCart.totalQuantity >= 2 
      && this.blockBuilderCart.items.find(item => item.type === 'block') 
      && this.blockDiscountPercentage
      && this.blockDiscountPercentage > 0) {
      const discountAmount = subtotal * (this.blockDiscountPercentage / 100);
      const discountedSubtotal = Shopify.formatMoney(subtotal - discountAmount);
      this.subtotal.innerHTML = `<s class="text-gray-56">${Shopify.formatMoney(subtotal)}</s> ${discountedSubtotal}`;
    } else {
      this.subtotal.textContent = Shopify.formatMoney(subtotal);
    }
  }

  onNavigationClick(event) {
    event.preventDefault();
    const page = event.target.getAttribute('data-bb-page');
    this.currentPage = this.querySelector(page);

    if (this.currentPage === this.blocksPage && this.blockSelectionIsOpen) {
      this.blockSelection.classList.add('hidden');
      this.blockSelectionIsOpen = false;
      this.blockCards.classList.remove('hidden');
    }

    if (this.currentPage === this.knivesPage) {
      this.knifeSelector.series = this.selectedSeries;
      this.knifeSelector.renderKnivesGrid(0)
      .then(() => {
        this.updatePage(this.currentPage);
        this.saveUserState();
      });
      return;
    }

    this.updatePage(this.currentPage);
    this.saveUserState();
  }

  onBackButtonClick(event) {
    event.preventDefault();
    const previousPage = this.pages[this.pages.indexOf(this.currentPage) - 1];

    window.scrollTo({
      top: this.currentPage.offsetTop,
      behavior: 'smooth'
    });

    if (this.currentPage === this.blocksPage && this.blockSelectionIsOpen) {
      this.blockSelection.classList.add('hidden');
      this.blockSelectionIsOpen = false;
      this.blockCards.classList.remove('hidden');
      this.updateFeaturedImage(this.blocksPage);
      this.saveUserState();

      this.resetFocus();
      return;
    } 

    if (this.currentPage === this.knivesPage) {
      const previousPageIndex = this.knifeSelector.currentKnivesPageIndex - 1;
      if (previousPageIndex >= 0) {
        this.knifeSelector.renderKnivesGrid(previousPageIndex);
        this.saveUserState();

        this.resetFocus();
        return;
      }
    }
    
    this.updatePage(previousPage);
    this.saveUserState();
  }

  onNextButtonClick(event) {
    event.preventDefault();
    const nextPage = this.pages[this.pages.indexOf(this.currentPage) + 1];

    window.scrollTo({
      top: this.currentPage.offsetTop,
      behavior: 'smooth'
    });
    
    if (this.currentPage === this.blocksPage) {
      if (this.blockSelectionIsOpen) {
        if (this.blockBuilderCart.items.length > 0 &&
          this.currentBlockSelector.selectedProduct &&
          !this.blockBuilderCart.items.find(item => item.productId === this.currentBlockSelector.selectedProduct.productId)
        ) {
          this.openWarningModal('continue')
          .then(() => {
            this.blockBuilderCart.items = [];
            this.updateDynamicContent();
            this.updateCartDrawer();
            this.disableNavItems([this.reviewPage]);

            this.addBlockToCart().then(() => {
              this.updateLoadingState();

              this.blockCards.classList.remove('hidden');
              this.blockSelection.classList.add('hidden');
              this.blockSelectionIsOpen = false;
              
              this.updatePage(nextPage);
              this.saveUserState();

              window.pushToDataLayer({
                event: this.gtmEvents['block-selected'],
              });
            });
          })
          
          return;
        } else {
          this.addBlockToCart().then(() => {
            this.updateLoadingState();
            this.blockCards.classList.remove('hidden');
            this.blockSelection.classList.add('hidden');
            this.blockSelectionIsOpen = false;
            this.saveUserState();

            window.pushToDataLayer({
              event: this.gtmEvents['block-selected'],
            });
          });
        }
      } else {
        this.blockCards.classList.add('hidden');
        this.blockSelection.classList.remove('hidden');
        this.blockSelectionIsOpen = true;
        this.sel
        this.setCurrentBlockSelector();
        this.updateFeaturedImage(this.blocksPage);
        this.saveUserState();

        this.resetFocus();
        return;
      }
    } else if (this.currentPage === this.knivesPage) {
      this.resetFocus();
      const nextPageIndex = this.knifeSelector.currentKnivesPageIndex + 1;
      if (nextPageIndex < this.knifeSelector.totalKnivesPages) {
        this.knifeSelector.renderKnivesGrid(nextPageIndex);
        this.saveUserState();
        return;
      }
    } else if (this.currentPage === this.seriesPage) {
      const selectedSeries = Array.from(this.seriesRadios).find(radio => radio.checked);
      if (!this.selectedSeries) {
        this.selectedSeries = selectedSeries.value;
        this.updatePage(nextPage);
        this.saveUserState();

        window.pushToDataLayer({
          event: this.gtmEvents['series-selected'],
        });
      } else {
        if (this.blockBuilderCart.items.length > 0 && selectedSeries.value !== this.selectedSeries) {
          this.openWarningModal('continue')
          .then(() => {
            this.blockBuilderCart.items = [];
            this.updateDynamicContent();
            this.updateCartDrawer();
            this.disableNavItems([this.knivesPage, this.reviewPage]);

            this.selectedSeries = selectedSeries.value;
            
            this.updatePage(nextPage);
            this.saveUserState();
          })
          
          return;
        }
      }
    }

    this.updatePage(nextPage);
    this.saveUserState();
  }

  onSkipButtonClick(event) {
    event.preventDefault();
    this.currentPage = this.pages[this.pages.length - 1];

    window.scrollTo({
      top: this.currentPage.offsetTop,
      behavior: 'smooth'
    });
    
    this.updatePage(this.currentPage);
    this.saveUserState();
  }

  updatePage(page) {
    this.currentPage = page;

    this.pages.forEach(page => {
      page.classList.add('hidden');
    });
    this.currentPage.classList.remove('hidden');
    this.backButton.disabled = this.currentPage === this.pages[0];
    this.nextButton.disabled = this.currentPage === this.pages[this.pages.length - 1];
    this.skipButton.disabled = this.currentPage !== this.knivesPage;
    this.navigationItems.forEach(item => {
      item.classList.remove('active');
    });
    const currentNavItem = this.navigationItems[this.pages.indexOf(this.currentPage)];
    currentNavItem.classList.add('active');
    currentNavItem.disabled = false;

    if (this.currentPage === this.reviewPage) {
      this.reviewItems.innerHTML = '';
      this.reviewErrors.innerHTML = '';
      if (this.bbCartDrawer.isOpen) {
        this.bbCartDrawer.toggleCartDrawer();
      }

      this.bbCartDrawer.renderCartItems().then(() => {
        this.reviewItems.innerHTML = this.bbCartDrawer.cartDrawerContent.innerHTML;
      });

      this.updateNavigatorBar(true);

      if (this.slotSelector) {
        this.slotSelector.clearCurrentSlots();
      }

      this.toggleSlotInfoBarCurrentSlot(false);
      this.toggleReviewAddMoreText();

      window.pushToDataLayer({
        event: this.gtmEvents['knives-selected'],
      });
    } else {
      if (this.currentPage === this.knivesPage && this.slotSelector && this.knifeSelector?.currentSlotCollectionHandle) {
        this.slotSelector.setCurrentSlots(this.knifeSelector.currentSlotCollectionHandle);
      }

      this.updateNavigatorBar(false);
      this.toggleSlotInfoBarCurrentSlot(true);
    }

    this.updateFeaturedImage(this.currentPage);

    this.resetFocus();
  }

  updateFeaturedImage(page) {
    this.imageContainers.forEach(container => {
      container.classList.add('hidden');
    });
    
    if (page === this.blocksPage && this.blockSelectionIsOpen) {
      this.blockSelectorImage.classList.remove('hidden');
    } else if (page === this.knivesPage || page === this.reviewPage) {
      this.slotSelectorImage.classList.remove('hidden');
      this.mobileSlotSelectorImage.classList.remove('hidden');
    } else {
      this.seriesImage.classList.remove('hidden');
    }
  }

  toggleSlotInfoBarCurrentSlot(isVisible = false) {
    this.querySelector('#bb-slot-info-bar-current-slot-container').classList.toggle('hidden', !isVisible);
    this.querySelector('#bb-slot-info-bar-current-slot-container-mobile').classList.toggle('hidden', !isVisible);
    this.querySelector('#bb-slot-info-bar-key--current-slot').classList.toggle('hidden', !isVisible);
    this.querySelector('#bb-slot-info-bar-key--current-slot-mobile').classList.toggle('hidden', !isVisible);
  }

  toggleReviewAddMoreText() {
    const totalSlotCount = Object.values(this.knifeSelector.bbStyle).reduce((acc, curr) => acc + curr, 0);
    const isVisible = (this.blockBuilderCart.totalQuantity - 1) < totalSlotCount;
    this.reviewAddMoreText.classList.toggle('hidden', !isVisible);
  }

  disableNavItems(pages = []) {
    pages.forEach(page => {
      const pageIndex = this.pages.indexOf(page);
      this.navigationItems[pageIndex].disabled = true;
    });
  }

  enableNavItems(pages = []) {
    pages.forEach(page => {
      const pageIndex = this.pages.indexOf(page);
      this.navigationItems[pageIndex].disabled = false;
    });
  }

  setCurrentBlockSelector() {
    this.currentBlockSelector = this.querySelector(`#bb-block-selector-${this.selectedBlockStyle}`);
    this.blockSelectors.forEach(selector => {
      selector.isActive = false;
      selector.classList.add('hidden');
    });
    this.currentBlockSelector.isActive = true;
    this.currentBlockSelector.classList.remove('hidden');
    this.currentBlockSelector.handleSelection();
  }

  onBlockStyleChange(event) {
    this.selectedBlockStyle = event.target.value;
  }

  async onBlockSelected(event) {
    const { image, slotCount } = event.detail;
    
    if (image) {
      this.updateBlockSelectorImage(image);
    }

    if (slotCount) {
      this.totalSlotCount = slotCount;
    }
  }

  async addBlockToCart() {
    if (!this.currentBlockSelector.selectedProduct || this.blockBuilderCart.items.find(item => item.productId === this.currentBlockSelector.selectedProduct.productId)) {
      return;
    }

    // Check if cart already has block by finding an items with type 'block'. remove it and add new one to front of array
    const oldBlock = this.blockBuilderCart.items.find(item => item.type === 'block');
    if (oldBlock) {
      this.blockBuilderCart.items.splice(this.blockBuilderCart.items.indexOf(oldBlock), 1);
    }

    this.blockBuilderCart.items.unshift({
      productId: this.currentBlockSelector.selectedProduct.productId,
      variantId: this.currentBlockSelector.selectedProduct.variantId,
      price: this.currentBlockSelector.selectedProduct.price,
      handle: this.currentBlockSelector.selectedProduct.handle,
      quantity: 1,
      type: 'block',
    });

    this.blockBuilderCart.total = this.blockBuilderCart.items.reduce((acc, item) => acc + item.price * item.quantity, 0);
    this.blockBuilderCart.totalQuantity = this.blockBuilderCart.items.reduce((acc, item) => acc + item.quantity, 0);

    const { selectedProduct: { handle: productHandle, style, styleTitle } } = this.currentBlockSelector;

    try {
      await this.renderSlotSelector(productHandle);
    } catch (error) {
      console.error('Error rendering slot selector:', error);
    }


    if (style) {
      this.knifeSelector.bbStyle = style;
      this.knifeSelector.bbStyleTitle = styleTitle;
      this.knifeSelector.series = this.selectedSeries;
      this.knifeSelector.totalKnivesPages = Object.keys(style).length;
      this.knifeSelector.renderKnivesGrid(0)
      .then(() => {
        this.updateCartDrawer();
      });
      return;
    }

    this.updateCartDrawer();
  }

  initKnifeSelector() {
    this.knifeSelector.series = this.selectedSeries;
    this.knifeSelector.bbStyle = this.selectedBlockStyle;
    this.knifeSelector.totalKnivesPages = Object.keys(this.selectedBlockStyle).length;
    this.knifeSelector.renderKnivesGrid(0);
  }

  onKnifeQtyChange(event) {
    const { productId, variantId, price, quantity, slotCollection, handle } = event.detail;
    const item = this.blockBuilderCart.items.find(item => item.productId === productId && item.variantId === variantId && item.slotCollection === slotCollection);
    if (item) {
      item.quantity = Number(quantity);
    } else {
      this.blockBuilderCart.items.push({ 
        productId, 
        variantId, 
        price,
        handle,
        slotCollection,
        quantity: Number(quantity),
      });
    }

    this.updateDynamicContent();
    this.updateCartDrawer();

    if (this.slotSelector) {
      const currentSlotQuantity = this.blockBuilderCart.items.filter(item => item.slotCollection === slotCollection).reduce((acc, item) => acc + item.quantity, 0);
      this.slotSelector.updateFilledSlots(currentSlotQuantity);
    }

    this.saveUserState();
  }

  updateDynamicContent() {
    this.blockBuilderCart.total = this.blockBuilderCart.items.reduce((acc, item) => acc + item.price * item.quantity, 0);
    this.blockBuilderCart.totalQuantity = this.blockBuilderCart.items.reduce((acc, item) => acc + item.quantity, 0);

    this.updateCartCount(this.blockBuilderCart.totalQuantity);
    this.updateSubtotal(this.blockBuilderCart.total);
  }

  updateCartDrawer() {
    if (this.bbCartDrawer) {
      this.bbCartDrawer.cartItems = this.blockBuilderCart.items;
      this.bbCartDrawer.handleCartItemsUpdate().then(() => {
        this.updateDynamicContent();
      });      
    }
  }

  updateNavigatorBar(isReviewPage = false) {
    if (isReviewPage) {
      this.nextButton.classList.add('hidden');
      this.skipButton.classList.add('hidden');
      this.addToCartButton.classList.remove('hidden');
      this.addToCartButton.disabled = false;

      if (this.bbCartDrawer && this.bbCartDrawer.cartButton) {
        this.bbCartDrawer.cartButton.disabled = true;
      }
    } else if (this.blockBuilderCart.totalQuantity > 0) {
      this.nextButton.classList.remove('hidden');
      this.skipButton.classList.remove('hidden');
      this.addToCartButton.classList.add('hidden');
      this.addToCartButton.disabled = false;

      if (this.bbCartDrawer && this.bbCartDrawer.cartButton) {
        this.bbCartDrawer.cartButton.disabled = false;
      }
    }
  }

  onAddToCart(event) {
    event.preventDefault();
    event.target.classList.add('loading');
    event.target.disabled = true;

    const formData = {
      'items': [],
    };

    this.blockBuilderCart.items.forEach(item => {
      formData.items.push({
        'id': item.variantId,
        'quantity': item.quantity,
        'properties': {
          '__blockBuilderDiscount': this.hasBlockBuilderDiscount(this.blockBuilderCart),
        }
      });
    });

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(errorData => {
          throw new Error(errorData.description || errorData.message || `HTTP error! status: ${response.status}`);
        });
      }
      return response.json();
    })
    .then(data => {
      this.shouldSaveStateOnLeave = false;
      this.clearAllCachedData();

      window.pushToDataLayer({
        event: this.gtmEvents['add-to-cart'],
      });

      window.location.href = window.Shopify.routes.root + 'cart';
    })
    .catch((error) => {
      if (error.status === 422) {
        this.reviewErrors.innerHTML = `<p>${error.message}</p>`;
      }
    });
  }

  hasBlockBuilderDiscount(cart) {
    // returns true if there is a block and any other item in the cart
    return cart.items.some(item => item.type === 'block' && cart.items.some(item => item.type !== 'block')) ? 'true' : 'false';
  }

  onCartItemRemoved(event) {
    const { variantId, isReviewPage } = event.detail;
    const itemIndex = this.blockBuilderCart.items.findIndex(cartItem => cartItem.variantId === parseInt(variantId));
    
    if (itemIndex !== -1) {
      this.blockBuilderCart.items.splice(itemIndex, 1);

      this.updateCartDrawer();
      this.knifeSelector.setQtySelectors();
      if (isReviewPage) {
        this.updatePage(this.reviewPage);
      }

      if (this.slotSelector) {
        this.slotSelector.clearAllSlots();
        this.slotSelector.setInitialFilledSlots(this.blockBuilderCart.items);
      }

      this.saveUserState();
    }
  }

  onChangeBlock(event) {
    this.openWarningModal('change-block').then(() => {
      if (this.bbCartDrawer.isOpen) {
        this.bbCartDrawer.toggleCartDrawer();
      }
      this.updatePage(this.blocksPage);
    });
  }

  renderSlotSelector(blockProductHandle) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();

      request.addEventListener('load', (event) => {
        try {
          this.handleSlotSelectorResponse(event);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
      
      request.addEventListener('error', () => {
        reject(new Error('Failed to load slot selector'));
      });

      request.open('GET', `${window.Shopify.routes.root}products/${blockProductHandle}?sections=block-builder-slot-selector`, true);
      request.send();
    });
  }

  handleSlotSelectorResponse(event) {
    const slotSelectorContent = JSON.parse(event.target.response)['block-builder-slot-selector'];
    const html = new DOMParser().parseFromString(slotSelectorContent, 'text/html');
    this.slotSelectorContainer.innerHTML = html.querySelector('#bb-slot-selector-content').innerHTML;
    this.slotSelector = this.slotSelectorContainer.querySelector('block-builder-slot-selector');
    this.slotSelector.setInitialFilledSlots(this.blockBuilderCart.items);
    
    // Handle responsive placement after content is loaded
    this.handleSlotSelectorResponsive();
  }

  onSlotSelected(event) {
    const { slotHandle } = event.detail;
    if (this.slotSelector) {
      this.slotSelector.setCurrentSlots(slotHandle);
    }

    if (this.slotInfoBarCurrentSlot) { 
      this.updateSlotInfoBarCurrentSlot(slotHandle);
    }
  }

  updateSlotInfoBarCurrentSlot(slotHandle) {
    const slotHandleKey = slotHandle.replaceAll('-', '_');
    if (window.blockBuilderStrings && window.blockBuilderStrings[slotHandleKey]) {
      this.slotInfoBarCurrentSlot.textContent = window.blockBuilderStrings[slotHandleKey];
      this.slotInfoBarCurrentSlotMobile.textContent = window.blockBuilderStrings[slotHandleKey];
    }
  }

  updateLoadingState() {
    // this.nextButton.classList.add('loading');
    // this.nextButton.disabled = true;
  }

  handleSlotSelectorResponsive() {
    const isMobile = window.innerWidth < 992;
    
    if (isMobile) {
      // Move content to mobile container if it's in desktop container
      if (this.slotSelectorContainer.children.length > 0) {
        while (this.slotSelectorContainer.firstChild) {
          this.mobileSlotSelectorContainer.appendChild(this.slotSelectorContainer.firstChild);
        }

        if (this.slotSelector) {
          this.slotSelector.setCurrentSlots(this.knifeSelector.currentSlotCollectionHandle);
          this.slotSelector.setInitialFilledSlots(this.blockBuilderCart.items);
          if (this.currentPage === this.reviewPage) {
            this.slotSelector.clearCurrentSlots();
          }
        }
      }
    } else {
      // Move content back to desktop container if it's in mobile container
      if (this.mobileSlotSelectorContainer.children.length > 0) {
        while (this.mobileSlotSelectorContainer.firstChild) {
          this.slotSelectorContainer.appendChild(this.mobileSlotSelectorContainer.firstChild);
        }

        if (this.slotSelector) {
          this.slotSelector.setCurrentSlots(this.knifeSelector.currentSlotCollectionHandle);
          this.slotSelector.setInitialFilledSlots(this.blockBuilderCart.items);
          if (this.currentPage === this.reviewPage) {
            this.slotSelector.clearCurrentSlots();
          }
        }
      }
    }
  }

  onReviewAddMoreButtonClick(event) {
    event.preventDefault();
    const emptySlotHandle = this.slotSelector.getFirstEmptySlotHandle();
    this.knifeSelector.currentSlotCollectionHandle = emptySlotHandle;
    const emptySlotIndex = emptySlotHandle ? Object.keys(this.knifeSelector.bbStyle).indexOf(emptySlotHandle.replace('bb-slot-', '').toUpperCase()) : 0;
    this.knifeSelector.renderKnivesGrid(emptySlotIndex);
    this.updatePage(this.knivesPage);
  }

  displayClearSelectionWarning() {
    this.clearSelectionWarning.classList.remove('hidden');
  }

  async openWarningModal(type = 'continue') {
    let resolveButton = null;
    let modalId = null;
    let modal = null;
    if (type === 'continue') {
      resolveButton = this.continueWarningModal.querySelector('#bb-warning-modal-continue-button');
      modalId = 'bb-warning-modal-continue';
      modal = this.continueWarningModal;
    } else if (type === 'pick-up') {
      resolveButton = this.pickUpWarningModal.querySelector('#bb-warning-modal-pick-up-button');
      modalId = 'bb-warning-modal-pick-up';
      modal = this.pickUpWarningModal;
    } else if (type === 'change-block') {
      resolveButton = this.changeBlockWarningModal.querySelector('#bb-warning-modal-change-block-button');
      modalId = 'bb-warning-modal-change-block';
      modal = this.changeBlockWarningModal;
    }
    
    return new Promise((resolve, reject) => {
      if (type === 'pick-up') {
        const state = this.userCache.get('userState');
        if (!this.hasCachedState() || !(state?.blockBuilderCart?.totalQuantity > 0)) {
          reject();
          return;
        }
      }

      modal.show(modalId);
    
      resolveButton.addEventListener('click', (event) => {
        event.preventDefault();
        modal.close(modalId);
        resolve();
      });

      modal.addEventListener('modal:closed', () => {
        reject();
      });
    });
  }

  saveUserState() {
    const state = {
      selectedSeries: this.selectedSeries,
      selectedBlockStyle: this.selectedBlockStyle,
      currentBlockSelector: this.currentBlockSelector,
      currentBlockProduct: this.currentBlockSelector?.selectedProduct || null,
      currentPageIndex: this.pages.indexOf(this.currentPage),
      blockBuilderCart: this.blockBuilderCart,
      timestamp: Date.now()
    };
    
    this.userCache.set('userState', state, 30 * 24 * 60 * 60 * 1000); // 30 days TTL
  }

  resetFocus() {
    // Only manage focus for keyboard users
    if (!this.usingKeyboard) return;
    
    if (this.currentPage === this.seriesPage) {
      this.seriesRadios[0].focus();
    } else if (this.currentPage === this.blocksPage) {
      if (this.blockSelectionIsOpen) {
        this.navigationItems[1].focus();
      } else {
        this.blockStyleRadios[0].focus();
      }
    } else if (this.currentPage === this.knivesPage) {
      this.navigationItems[2].focus();
    } else if (this.currentPage === this.reviewPage) {
      this.navigationItems[3].focus();
    }
  }

  restoreUserState() {
    const state = this.userCache.get('userState');
    
    if (state && state.timestamp) {
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
      if (Date.now() - state.timestamp < maxAge) {
        this.selectedSeries = state.selectedSeries;
        this.selectedBlockStyle = state.selectedBlockStyle;
        this.currentBlockSelector = state.currentBlockSelector;
        this.blockBuilderCart = state.blockBuilderCart || {
          items: [],
          total: 0,
          totalQuantity: 0,
        };

        // Restore series selection
        if (this.selectedSeries) {
          const seriesRadio = Array.from(this.seriesRadios).find(radio => radio.value === this.selectedSeries);
          if (seriesRadio) {
            seriesRadio.checked = true;
          }
        }

        // Restore block style selection
        if (this.selectedBlockStyle) {
          const blockStyleRadio = Array.from(this.blockStyleRadios).find(radio => radio.value === this.selectedBlockStyle);
          if (blockStyleRadio) {
            blockStyleRadio.checked = true;
          }
        }

        if (state.currentBlockProduct && this.currentBlockSelector) {
          this.currentBlockSelector.selectedProduct = state.currentBlockProduct;
        }

        // Restore page state
        if (state.currentPageIndex >= 0 && state.currentPageIndex < this.pages.length) {
          this.currentPage = this.pages[state.currentPageIndex];
        }

        // Update UI to reflect restored state
        this.updateDynamicContent();
        this.updateCartDrawer();
        this.updatePage(this.currentPage);

        // Restore navigation items
        const pagesToEnable = this.pages.slice(0, state.currentPageIndex + 1);
        this.enableNavItems(pagesToEnable);

        // Restore knife selector state
        if (this.knifeSelector) {
          this.knifeSelector.series = this.selectedSeries;
          this.knifeSelector.bbStyle = this.selectedBlockStyle;
          if (this.knifeSelector.initKnifeSelector) {
            this.knifeSelector.initKnifeSelector();
          }
        }

        // Restore slot selector state
        if (this.currentBlockSelector && this.currentBlockSelector.selectedProduct) {
          this.renderSlotSelector(this.currentBlockSelector.selectedProduct.handle).then(() => {
            if (this.slotSelector) {
              this.slotSelector.setCurrentSlots(this.knifeSelector.currentSlotCollectionHandle);
              if (this.currentPage === this.reviewPage) {
                this.slotSelector.clearCurrentSlots();
              }
            }
          });
        }
      } else {
        // State is too old, clear it
        this.userCache.delete('userState');
      }
    }
  }

  clearUserState() {
    this.userCache.delete('userState');
  }

  // Utility method to manually clear all cached data
  clearAllCachedData() {
    this.userCache.clear();
    this.userCache.clearStorage();
  }

  // Method to check if there's cached state available
  hasCachedState() {
    const state = this.userCache.get('userState');
    return state && state.timestamp && (Date.now() - state.timestamp) < (24 * 60 * 60 * 1000);
  }
}

customElements.define('block-builder', BlockBuilder);

const blockMaps = new WeakMap();
const productStylesMaps = new WeakMap();
const stylesTitlesMaps = new WeakMap();

class BlockSelector extends HTMLElement {
  constructor() {
    super();
    this.slots = this.querySelectorAll('[data-bb-slot]');
    this.colors = this.querySelectorAll('[data-bb-color]');

    this.price = this.querySelector('.bb-block-selector-price');
    this.slotsLabel = this.querySelector('.bb-block-selector-slots-label .slots-label-number');
    this.colorText = this.querySelector('.bb-block-selector-color-text');
    this.yotpoStars = this.querySelector('.bb-block-selector-yotpo-stars');
    this.productAccordions = this.querySelector('#bb-block-selector-product-accordions');

    const blockMap = {};
    const products = JSON.parse(this.dataset.blockMap);
    const productStyles = JSON.parse(this.dataset.productStylesMap);
    const stylesTitles = JSON.parse(this.dataset.stylesTitlesMap);

    products.forEach((product, pIndex) => {
      product.variants.forEach((variant) => {
        const color = variant.option1.toLowerCase().replace(' ', '-');
        const slot = variant.option2;

        if (slot && color) {
          blockMap[`${slot}-${color}`] = {
            productId: product.id,
            variantId: variant.id,
            handle: product.handle,
            price: variant.price,
            image: variant.featured_image ? variant.featured_image.src : product.featured_image,
            available: variant.available,
            style: JSON.parse(decodeURIComponent(productStyles[pIndex])),
            styleTitle: stylesTitles[pIndex]
          };
        }
      });
    });

    blockMaps.set(this, blockMap);

    this.slots.forEach(slot => {
      slot.addEventListener('click', this.onSlotClick.bind(this));
    });

    this.colors.forEach(color => {
      color.addEventListener('click', this.onColorClick.bind(this));
    });
  }
  
  selectedSlot = null;
  selectedColor = null;
  selectedProduct = null;
  isActive = false;
  bbFeaturedImage = null;

  connectedCallback() {
    this.classList.toggle('hidden', !this.isActive);
    this.selectedSlot = this.slots[0].dataset.bbSlot;
    this.selectedColor = this.colors[0].dataset.bbColor;
    this.handleSelection();
  }

  getBlockMap() {
    return blockMaps.get(this);
  }

  handleSelection() {
    const blockMap = this.getBlockMap();
    const key = `${this.selectedSlot}-${this.selectedColor}`;
    this.selectedProduct = blockMap[key];
    this.updateSwatchAvailability();
    
    if (this.selectedProduct) {
      this.updateDynamicContent();
      this.dispatchEvent(new CustomEvent('block-selected', {
        detail: {
          product: this.selectedProduct,
          image: this.selectedProduct?.image || '',
          style: this.selectedProduct?.style || {},
          styleTitle: this.selectedProduct?.styleTitle || '',
          slotCount: this.selectedSlot,
        }
      }));
    }
  }

  updateSwatchAvailability() {
    const blockMap = this.getBlockMap();
    
    // Reset all colors to enabled state first
    this.colors.forEach(color => {
      const colorButton = color;
      colorButton.disabled = false;
      colorButton.classList.remove('sold-out', 'not-valid');
    });

    // For each color, check if it exists and is available for the current slot
    this.colors.forEach(color => {
      const colorButton = color;
      const colorValue = color.dataset.bbColor;
      const key = `${this.selectedSlot}-${colorValue}`;
      const variant = blockMap[key];

      if (!variant || !variant.available) {
        const unavailableClass = variant ? 'sold-out' : 'not-valid';
        colorButton.disabled = true;
        colorButton.classList.add(unavailableClass);
        colorButton.classList.remove('active');

        // If this was the selected color, find the next available one
        if (colorValue === this.selectedColor) {
          const nextAvailableColor = Array.from(this.colors).find(c => {
            const cValue = c.dataset.bbColor;
            const cKey = `${this.selectedSlot}-${cValue}`;
            const cVariant = blockMap[cKey];
            return cVariant && cVariant.available;
          });

          if (nextAvailableColor) {
            this.selectedColor = nextAvailableColor.dataset.bbColor;
            nextAvailableColor.classList.add('active');
            this.selectedProduct = blockMap[`${this.selectedSlot}-${this.selectedColor}`];
          } else {
            this.selectedColor = null;
            this.selectedProduct = null;
          }
        }
      }
    });
  }

  onSlotClick(event) {
    this.selectedSlot = event.target.dataset.bbSlot;
    this.slots.forEach(slot => {
      slot.classList.remove('active');
    });
    event.target.classList.add('active');
    this.handleSelection();
  }

  onColorClick(event) {
    this.selectedColor = event.currentTarget.dataset.bbColor;
    this.colors.forEach(color => {
      color.classList.remove('active');
    });
    event.currentTarget.classList.add('active');
    this.handleSelection();
  }

  updateDynamicContent() {
    if (this.selectedProduct) {
      this.renderProductAccordions();
      this.price.textContent = Shopify.formatMoney(this.selectedProduct?.price);
      this.slotsLabel.textContent = this.slotsLabel.textContent.replace(/\d+/g, this.selectedSlot);
      this.colorText.textContent = this.selectedColor.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    }
  }

  renderProductAccordions() {
    if (this.selectedProduct && this.productAccordions && this.selectedProduct.handle) {
      const request = new XMLHttpRequest();
      request.addEventListener('load', this.handleProductAccordionsResponse.bind(this));
      request.open('GET', `${window.Shopify.routes.root}products/${this.selectedProduct.handle}?sections=product-accordions`, true);
      request.send();
    }
  }

  handleProductAccordionsResponse(event) {
    try {
      const productAccordions = JSON.parse(event.target.response)['product-accordions'];
      const html = new DOMParser().parseFromString(productAccordions, 'text/html');
      this.productAccordions.innerHTML = html.querySelector('#product-accordions').innerHTML;
    } catch (error) {
      console.error('Error parsing Product Accordions HTML', error);
    }
  }
}

customElements.define('bb-block-selector', BlockSelector);

class KnifeSelector extends HTMLElement {
  constructor() {
    super();
    this.knivesGrid = this.querySelector('#bb-knives-grid');
    this.slotsText = this.querySelector('#bb-knives-selector-slots-text');
    this.productModal = this.querySelector('#bb-product-modal');
    this.blockBuilder = this.closest('block-builder');

    this.ignoreSeriesCollections = ['bb-slot-f', 'bb-slot-h', 'bb-slot-s'];
  }

  series = null;
  bbStyle = {};
  currentAvailableSlots = null;
  currentSlotCollectionHandle = null;
  currentKnivesPageIndex = 0;
  totalKnivesPages = 0;
  qtySelectors = [];
  blockBuilderCart = null;
  modalTriggers = [];
  currentProductModalHandle = null;
  customSlotTextHandles = ['bb-slot-f', 'bb-slot-h', 'bb-slot-s'];

  connectedCallback() {
    this.knivesGrid.innerHTML = '';
    if (this.blockBuilder 
      && this.blockBuilder.slotInfoBarCurrentSlot
      && this.blockBuilder?.currentBlockSelector?.selectedProduct?.style
      && this.blockBuilder?.currentBlockSelector?.selectedProduct?.styleTitle) {
      this.initKnifeSelector();
    }
  }

  initKnifeSelector() {
    this.bbStyle = this.blockBuilder.currentBlockSelector.selectedProduct.style;
    this.bbStyleTitle = this.blockBuilder.currentBlockSelector.selectedProduct.styleTitle;
    this.currentSlotText = this.blockBuilder.slotInfoBarCurrentSlot;
    this.series = this.blockBuilder.selectedSeries;
    this.totalKnivesPages = Object.keys(this.bbStyle).length;
    this.setCollectionAndSlots(0);
    this.renderKnivesGrid();
  }

  renderKnivesGrid(index = 0) {
    return new Promise((resolve, reject) => {
      this.currentKnivesPageIndex = index;
      this.setCollectionAndSlots(index);

      const slotTitle = this.currentSlotCollectionHandle.replace('bb-slot-', '').replace('-', '.').toUpperCase();
      const styleTitle = this.bbStyleTitle.replace(/\d/g, '').toUpperCase();
      const styleString = `filter.v.m.custom.block_builder_slots_styles=${styleTitle + slotTitle}&`;
      let seriesString = '';
      if (this.series && !this.ignoreSeriesCollections.includes(this.currentSlotCollectionHandle)) {
        seriesString = `filter.p.m.global.series=${deHandleize(this.series)}&`;
      }
      const request = new XMLHttpRequest();

      request.addEventListener('load', (event) => {
        try {
          this.handleKnifeGridResponse(event);
          resolve();
        } catch (error) {
          console.error('Error rendering knives grid', error);
          reject(error);
        }
      });
      request.open('GET', `${window.Shopify.routes.root}collections/all?${styleString}${seriesString}sections=block-builder-knives-collection`, true);
      request.send();
    });
  }

  handleKnifeGridResponse(event) {
    const knivesCollection = JSON.parse(event.target.response)['block-builder-knives-collection'];
    const html = new DOMParser().parseFromString(knivesCollection, 'text/html');
    this.knivesGrid.innerHTML = html.querySelector('#bb-knives-collection').innerHTML;
    this.updateSlotsText();
    this.setQtySelectors();
    this.setModalTriggers();
    this.setCurrentSlotText();
  }

  setCollectionAndSlots(index) {
    this.currentSlotCollectionHandle = (`bb-slot-${Object.keys(this.bbStyle)[index]}`).replace(/\./g, '-').toLowerCase();
    this.currentAvailableSlots = Object.values(this.bbStyle)[index];

    this.dispatchEvent(new CustomEvent('bb-slot-selected', {
      detail: {
        slotHandle: this.currentSlotCollectionHandle,
      }
    }));
  }

  updateSlotsText() {
    if (this.currentAvailableSlots && this.slotsText) {
      if (this.customSlotTextHandles.includes(this.currentSlotCollectionHandle)) {
        this.slotsText.textContent = window.blockBuilderStrings[`bb_slot_select_${this.currentSlotCollectionHandle.replace('bb-slot-', '')}`];
      } else {
        if (this.currentAvailableSlots === 1) {
          this.slotsText.textContent = window.blockBuilderStrings['bb_slot_select_default_singular'];
        } else {
          this.slotsText.textContent = window.blockBuilderStrings['bb_slot_select_default'].replace('#', this.currentAvailableSlots);
        }
      }
    }
  }

  setQtySelectors() {
    this.qtySelectors = this.querySelectorAll('block-builder-qty-selector');
    this.qtySelectors.forEach(selector => {
      if (!selector.slotCollection) {
        selector.slotCollection = this.currentSlotCollectionHandle;
      }
      selector.removeEventListener('bb-knife-qty-changed', this.onQtySelectorChange.bind(this));
      selector.addEventListener('bb-knife-qty-changed', this.onQtySelectorChange.bind(this));

      if (!this.blockBuilderCart) {
        this.blockBuilderCart = this.closest('block-builder').blockBuilderCart;
      }
      
      // Sync quantity from blockBuilderCart if available
      const productId = selector.productId;
      const variantId = selector.variantId;
      const slotCollection = selector.slotCollection;
      const cartItem = this.blockBuilderCart.items.find(item => 
        item.productId === productId && item.variantId === variantId && item.slotCollection === slotCollection
      );
      if (cartItem) {
        selector.querySelector('input').value = cartItem.quantity;
        selector.updateMinusButtonState();
      } else {
        selector.querySelector('input').value = 0;
        selector.updateMinusButtonState();
      }
    });
    this.updatePlusButtonStates();
  }

  onQtySelectorChange(event) {
    this.dispatchEvent(new CustomEvent('bb-knife-qty-changed', {
      detail: {
        productId: event.detail.productId,
        variantId: event.detail.variantId,
        handle: event.detail.handle,
        price: event.detail.price,
        quantity: event.detail.quantity,
        slotCollection: event.detail.slotCollection,
      }
    }));

    this.updatePlusButtonStates(event.detail.type === 'plus' ? 'max' : 'min');
  }

  updatePlusButtonStates(type = 'max') {
    const variantQuantities = new Map();
  
    // Sum quantities for each unique variant ID
    Array.from(this.qtySelectors).forEach(selector => {
      const { variantId, slotCollection } = selector;
      const variantMapKey = `${variantId}-${slotCollection}`;
      const currentQty = parseInt(selector.querySelector('input').value || 0);
      
      if (variantQuantities.has(variantMapKey)) {
        // If we already have this variant, use the higher quantity
        const existingQty = variantQuantities.get(variantMapKey);
        if (type === 'max') {
          variantQuantities.set(variantMapKey, Math.max(existingQty, currentQty));
        } else {
          variantQuantities.set(variantMapKey, Math.min(existingQty, currentQty));
        }
      } else {
        variantQuantities.set(variantMapKey, currentQty);
      }
    });
    
    // Calculate total from unique variants
    const totalQuantity = Array.from(variantQuantities.values()).reduce((total, qty) => total + qty, 0);
    
    this.qtySelectors.forEach(selector => {
      selector.updatePlusButtonState(totalQuantity >= this.currentAvailableSlots);
    });
  }

  setModalTriggers() {
    this.modalTriggers = this.knivesGrid.querySelectorAll('[data-modal-trigger]');
    this.modalTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleModalTriggerClick.bind(this));
    });
    this.productModal.addEventListener('closed', this.handleModalClose.bind(this));
  }

  handleModalTriggerClick(event) {
    const productHandle = event.currentTarget.dataset.productHandle;
    const variantId = event.currentTarget.dataset.variantId;
    const variantString = variantId ? `?variant=${variantId}&` : '?';
    if (this.currentProductModalHandle !== productHandle) {
      const request = new XMLHttpRequest();
      request.addEventListener('load', this.handleModalResponse.bind(this));
      request.open('GET', `${window.Shopify.routes.root}products/${productHandle}${variantString}sections=bb-product-modal-content`, true);
      request.send();
    } else {
      this.setQtySelectors();
      this.productModal.show('bb-product-modal');
    }
    this.currentProductModalHandle = productHandle;
  }

  handleModalResponse(event) {
    const productModalContent = JSON.parse(event.target.response)['bb-product-modal-content'];
    const html = new DOMParser().parseFromString(productModalContent, 'text/html');
    
    this.productModal.querySelector('#product-modal-content').innerHTML = html.querySelector('#product-modal-content').innerHTML;
    this.setQtySelectors();

    this.productModal.show('bb-product-modal');
  }

  handleModalClose() {
    this.setQtySelectors();
  }

  setCurrentSlotText() {
    if (this.currentSlotText) {
      const slotKey = this.currentSlotCollectionHandle.replace(/\-/g, '_');
      const slotText = window.blockBuilderStrings[`${slotKey}`];
      this.currentSlotText.textContent = slotText;
    }
  }
}

customElements.define('bb-knife-selector', KnifeSelector);

function deHandleize(string) {
  return string.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}
